import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { IconBellRinging, IconTrash } from "@tabler/icons-react";
import { Alert, Badge, Button, Group, IconButton, Paper, Stack, Text } from "@/components/ui";
import { qk } from "@/lib/query-keys";
import { toastErrorApi, toastExito } from "@/lib/toast";
import { formatFechaHora } from "@/shared/format/fecha";
import { useEliminarSuscripcionPush } from "../hooks/use-perfil-notificaciones";
import { desactivarPushEndpoint, registrarSuscripcionPush, type MisCanales } from "../services/perfil-notificaciones.api";
import {
  activarPush,
  desactivarPush,
  estadoPushNavegador,
  nombreNavegador,
  suscripcionActual,
  type EstadoPushNavegador,
} from "../utils/push-navegador";

/** Notificaciones del navegador (Web push): activar en este equipo y ver/quitar los demás dispositivos. */
export function CardPush({ push }: { push: MisCanales["push"] }) {
  const qc = useQueryClient();
  const quitar = useEliminarSuscripcionPush();
  const [estado, setEstado] = useState<EstadoPushNavegador>("inactivo");
  const [miEndpoint, setMiEndpoint] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);

  const refrescarEstado = async () => {
    setEstado(await estadoPushNavegador());
    setMiEndpoint((await suscripcionActual())?.endpoint ?? null);
  };

  useEffect(() => {
    void refrescarEstado();
  }, []);

  const activar = async () => {
    if (!push.clavePublica) return;
    setTrabajando(true);
    try {
      const sub = await activarPush(push.clavePublica);
      await registrarSuscripcionPush({ ...sub, navegador: nombreNavegador() });
      toastExito({ title: "Notificaciones activadas en este navegador" });
    } catch (err) {
      toastErrorApi(err);
    } finally {
      await refrescarEstado();
      void qc.invalidateQueries({ queryKey: qk.misCanalesNotif });
      setTrabajando(false);
    }
  };

  const desactivar = async () => {
    setTrabajando(true);
    try {
      const endpoint = await desactivarPush();
      if (endpoint) await desactivarPushEndpoint(endpoint).catch(() => undefined);
      toastExito({ title: "Notificaciones desactivadas en este navegador" });
    } catch (err) {
      toastErrorApi(err);
    } finally {
      await refrescarEstado();
      void qc.invalidateQueries({ queryKey: qk.misCanalesNotif });
      setTrabajando(false);
    }
  };

  return (
    <Paper withBorder padding="lg">
      <Stack gap="sm">
        <Group justify="space-between">
          <Group gap="xs">
            <IconBellRinging size={18} className="text-multiva-600" />
            <Text fw={600}>Notificaciones del navegador</Text>
          </Group>
          {estado === "activo" ? <Badge color="exito">Activas aquí</Badge> : null}
        </Group>

        {!push.disponible ? (
          <Text size="sm" c="dimmed">Las notificaciones del navegador aún no están configuradas en el portal.</Text>
        ) : estado === "no-soportado" ? (
          <Text size="sm" c="dimmed">Este navegador no admite notificaciones push (o el portal no se abrió con https).</Text>
        ) : estado === "bloqueado" ? (
          <Alert variant="warning" title="Bloqueadas en este navegador">
            Diste "Bloquear" al permiso de notificaciones. Actívalo desde el candado de la barra de direcciones y vuelve a intentar.
          </Alert>
        ) : (
          <Group gap="xs">
            <Text size="sm" c="dimmed" className="flex-1">
              Avisos de escritorio aunque el portal esté en otra pestaña o minimizado.
            </Text>
            {estado === "activo" ? (
              <Button variant="default" size="sm" loading={trabajando} onClick={() => void desactivar()}>Desactivar aquí</Button>
            ) : (
              <Button size="sm" loading={trabajando} onClick={() => void activar()}>Activar en este navegador</Button>
            )}
          </Group>
        )}

        {push.suscripciones.length > 0 ? (
          <div>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" className="mb-1">Dispositivos con notificaciones</Text>
            <ul className="divide-y divide-border rounded-md border border-border">
              {push.suscripciones.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                  <div>
                    <span className="font-medium">{s.navegador ?? "Navegador"}</span>
                    <Text size="xs" c="dimmed">
                      Desde {formatFechaHora(s.creadoAt)}
                      {s.ultimoEnvioAt ? ` · último aviso ${formatFechaHora(s.ultimoEnvioAt)}` : ""}
                    </Text>
                  </div>
                  <IconButton
                    size="sm"
                    aria-label="Quitar dispositivo"
                    title="Quitar dispositivo"
                    disabled={quitar.isPending}
                    onClick={() => quitar.mutate(s.id, { onSuccess: () => void refrescarEstado() })}
                  >
                    <IconTrash />
                  </IconButton>
                </li>
              ))}
            </ul>
            {miEndpoint ? <Text size="xs" c="dimmed" className="mt-1">Si quitas este navegador desde aquí, desactívalo también con el botón de arriba.</Text> : null}
          </div>
        ) : null}
      </Stack>
    </Paper>
  );
}
